// =============================================================================
// features/sales/campaigns/CampaignsIndicators.tsx — Faixa de indicadores
// (ativas, pessoas a trabalhar, retornos hoje, taxa de contato, conversões —
// doc 26 §6.5).
//
// "Ativas" é o ÚNICO indicador que a API garante hoje: `GET
// /api/sales/campaigns?status=active&limit=1` e o `total` da resposta (fonte
// real, sem cálculo no cliente — mesmo padrão de
// features/sales/agenda/AgendaIndicators.tsx). É clicável: aplica o filtro
// de status=active na URL (F35-S07 DoD: filtros refletidos na URL).
//
// Os outros 4 dependem de `campaign_members`/`contact_attempts` agregados,
// que nenhum endpoint publica ainda (ver header de hooks/sales/useCampaigns.ts
// para o levantamento completo). Mostrar um número aqui seria calcular no
// cliente — proibido pela norma do módulo ("Indicadores vêm da API, sem
// cálculo duplicado"). Em vez de esconder o card ou fabricar um valor, cada
// um aparece com "—" e o motivo explícito, para o gestor nunca confundir
// "zero" com "não medido ainda".
// =============================================================================

import * as React from 'react';

import { Stat } from '../../../components/ui/Stat';
import { useCampaignsCount } from '../../../hooks/sales/useCampaigns';

export interface CampaignsIndicatorsProps {
  enabled: boolean;
  activeSelected: boolean;
  onSelectActive: () => void;
}

export function CampaignsIndicators({
  enabled,
  activeSelected,
  onSelectActive,
}: CampaignsIndicatorsProps): React.JSX.Element {
  const active = useCampaignsCount({ status: ['active'] }, { enabled });

  const pendingIndicators: { label: string; description: string }[] = [
    {
      label: 'Pessoas a trabalhar',
      description: 'Pendente: agregado de campaign_members ainda não publicado pela API.',
    },
    {
      label: 'Retornos hoje',
      description: 'Pendente: agregado de retornos vinculados à campanha ainda não publicado.',
    },
    {
      label: 'Taxa de contato',
      description: 'Pendente: agregado de tentativas de contato ainda não publicado.',
    },
    {
      label: 'Conversões',
      description: 'Pendente: agregado de conversões ainda não publicado.',
    },
  ];

  return (
    <div
      className="flex gap-4 overflow-x-auto snap-x pb-1 sm:pb-0 sm:grid sm:grid-cols-5 sm:overflow-visible"
      role="group"
      aria-label="Indicadores de campanhas"
    >
      <button
        type="button"
        onClick={onSelectActive}
        aria-pressed={activeSelected}
        className="min-w-[200px] sm:min-w-0 snap-start shrink-0 sm:shrink text-left rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40"
        title="Filtrar por campanhas ativas"
      >
        <Stat
          label="Ativas"
          value={active.isError ? '—' : (active.total ?? '—')}
          description={
            activeSelected ? 'Filtro aplicado — clique para limpar' : 'Clique para filtrar'
          }
          {...(activeSelected ? { className: 'border-azul' } : {})}
        />
      </button>

      {pendingIndicators.map((item) => (
        <div key={item.label} className="min-w-[200px] sm:min-w-0 snap-start shrink-0 sm:shrink">
          <Stat label={item.label} value="—" description={item.description} />
        </div>
      ))}
    </div>
  );
}
